import api from './api';
import { saveBlob } from '../utils/saveFile';

/**
 * Accounting Reports — live V1 Invoice Lag, Invoicing Reports and XLS Invoice.
 * Backed by the read-only routes in accounting_reports.py. Dates are YYYY-MM-DD;
 * absent dates select Invoice Lag's V1 not-invoiced mode.
 */
export type InvoiceLagGroupBy = 'division' | 'billto' | 'customer' | 'invoicedby';
export interface InvoiceLagCriteria {
  start?: string;
  end?: string;
  billto?: number;
  customer?: number;
  groupby?: InvoiceLagGroupBy;
}
export interface InvoiceLagDetailRow {
  pronumber: string;
  masterpronum: string | null;
  invoicenumber: string | null;
  accessorialnum: number | null;
  division: string;
  movetype: string;
  billto: string | null;
  mas90code: string | null;
  customer: string | null;
  container: string | null;
  bol: string | null;
  csr: string | null;
  whsecsr: string | null;
  invoicedby: string | null;
  createdate: string | null;
  deliverydate: string | null;
  terminatedate: string | null;
  procompletedate: string | null;
  mastercompdate: string | null;
  protomasterdays: number | null;
  senttoaccdate: string | null;
  mastosentaccdays: number | null;
  invoicedate: string | null;
  senttoaccinvdays: number | null;
  masterinvoicedays: number | null;
  numinvtickets: number;
  ttresolutiontime: number | null;
  invoicetotal: number | null;
  creditamt: number | null;
}
export interface InvoiceLagGroupRow {
  group: string;
  avginvoicelag: number;
  numinvtickets: number;
  ttresolutiontime: number;
  invoicetotal: number;
  creditamt: number;
  numinvoices: number;
}
export type InvoiceLagRow = InvoiceLagDetailRow | InvoiceLagGroupRow;
export type InvoiceLagResponse = {
  criteria: string;
  truncated: boolean;
} & ({ mode: 'detail'; groupBy: null; rows: InvoiceLagDetailRow[] }
  | { mode: 'grouped'; groupBy: InvoiceLagGroupBy; rows: InvoiceLagGroupRow[] });
export interface AverageDaysRow {
  createdby: string;
  invoicedate: string | null;
  invoicenumber: string;
  customer: string | null;
  csr: string | null;
  lastdelivery: string | null;
  invoicetotal: number | null;
  daysdiff: number | null;
}
export interface ClerkSubtotal { clerk: string; average: number; count: number }
export interface AverageDaysResponse {
  rows: AverageDaysRow[];
  clerkSubtotals: ClerkSubtotal[];
  overallAverage: number;
  truncated: boolean;
}
export interface InvoiceClerk { username: string; fullName: string }
export interface AverageDaysCriteria { start: string; end: string; clerk?: string }

export type InvoicingActivityGroupBy = 'DAY' | 'WEEK' | 'MONTH';
export interface InvoicingActivityMetrics { pronumbers: number; invoices: number; amount: number }
export interface InvoicingActivityRow {
  username: string;
  clerk: string;
  periods: Record<string, InvoicingActivityMetrics>;
  total: InvoicingActivityMetrics;
}
export interface InvoicingActivityResponse {
  groupBy: InvoicingActivityGroupBy;
  criteria: string;
  periods: string[];
  rows: InvoicingActivityRow[];
  periodTotals: Record<string, InvoicingActivityMetrics>;
  grandTotal: InvoicingActivityMetrics;
}
export interface InvoicingActivityCriteria { start: string; end: string; groupby: InvoicingActivityGroupBy }

/**
 * Credit Memo Report — reports_cms.pl::creditmemoreport. One row per rebill/credit LINE
 * in a `rebilldate` range, so a record with three GL codes is three rows.
 */
export interface CreditMemoReportRow {
  pronumber: number;
  creditmemonum: string;
  createdby: string;
  createdatetime: string | null;
  type: string;
  rebilldate: string | null;
  glcode: string;
  gldescription: string | null;
  amount: number;
  rebilledinvoice: string;
  applyto: string;
  mastag: string;
  billtoname: string | null;
  deptresponsible: string;
  preventable: string;
  comments: string;
  /** Null until the credit has been exported to Sage. */
  masexportdate: string | null;
}
export interface CreditMemoReportResponse {
  criteria: string;
  /** Sum of the returned rows' amounts — after the cap, so it matches what is shown. */
  total: number;
  truncated: boolean;
  rows: CreditMemoReportRow[];
}
export interface CreditMemoReportCriteria { start: string; end: string }

const base = '/accounting-reports';
const accountingReportsService = {
  getInvoicingActivity: async (params: InvoicingActivityCriteria): Promise<InvoicingActivityResponse> =>
    (await api.get<InvoicingActivityResponse>(`${base}/invoicing-activity`, { params })).data,
  getInvoiceLag: async (params: InvoiceLagCriteria): Promise<InvoiceLagResponse> =>
    (await api.get<InvoiceLagResponse>(`${base}/invoice-lag`, { params })).data,
  getAverageDaysToInvoice: async (params: AverageDaysCriteria): Promise<AverageDaysResponse> =>
    (await api.get<AverageDaysResponse>(`${base}/average-days-to-invoice`, { params })).data,
  getInvoiceClerks: async (): Promise<InvoiceClerk[]> =>
    (await api.get<InvoiceClerk[]>(`${base}/invoice-clerks`)).data,
  getCreditMemoReport: async (params: CreditMemoReportCriteria): Promise<CreditMemoReportResponse> =>
    (await api.get<CreditMemoReportResponse>(`${base}/credit-memo`, { params })).data,
  /** Returns null on a cancelled save, so the page never reports a false download. */
  downloadXlsInvoice: async (invoiceNumber: string): Promise<string | null> => {
    invoiceNumber = invoiceNumber.replace(/[^a-z0-9]/gi, '');
    const response = await api.get<Blob>(`${base}/xls-invoice/${encodeURIComponent(invoiceNumber)}`, { responseType: 'blob' });
    const disposition = String(response.headers['content-disposition'] || '');
    const filename = /filename="([^"]+)"/i.exec(disposition)?.[1]
      || /filename=([^;\s]+)/i.exec(disposition)?.[1]
      || `INVOICE_${invoiceNumber.replace(/[^a-z0-9]/gi, '')}.xlsx`;
    return await saveBlob(response.data, filename) ? filename : null;
  },
};
export default accountingReportsService;
